<script setup>
// Right Chat - coluna (etapa) do Kanban proprio.
//
// O drag-and-drop e HTML5 nativo de proposito: uma lib nova (vuedraggable e afins)
// obrigaria a mexer em package.json/pnpm-lock.yaml do upstream, e conflito de lockfile
// a cada versao do Chatwoot custa mais que os ~40 linhas de dragover/drop daqui.
import { ref, computed } from 'vue';
import RightKanbanCard from './RightKanbanCard.vue';

const props = defineProps({
  step: { type: Object, required: true },
  tasks: { type: Array, default: () => [] },
  accountId: { type: [String, Number], required: true },
  draggingTaskId: { type: Number, default: null },
});

const emit = defineEmits(['drop-card', 'drag-start', 'drag-end', 'edit-step']);

const hoverBeforeId = ref(null);
const isOver = ref(false);

const totalValue = computed(() =>
  props.tasks.reduce((sum, task) => sum + (Number(task.value) || 0), 0)
);

const formattedTotal = computed(() =>
  totalValue.value
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      }).format(totalValue.value)
    : ''
);

const onCardDragStart = (task, event) => {
  // `effectAllowed` + payload no dataTransfer: sem isso o Firefox nao inicia o drag.
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(task.id));
  }
  emit('drag-start', task);
};

const onCardDragEnd = () => {
  hoverBeforeId.value = null;
  isOver.value = false;
  emit('drag-end');
};

const onCardDragOver = (task, event) => {
  event.preventDefault();
  if (task.id === props.draggingTaskId) return;
  hoverBeforeId.value = task.id;
  isOver.value = true;
};

const onColumnDragOver = event => {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  isOver.value = true;
};

const onColumnDragLeave = event => {
  // `relatedTarget` fora da coluna evita o flicker quando o ponteiro passa por um card.
  if (event.currentTarget.contains(event.relatedTarget)) return;
  hoverBeforeId.value = null;
  isOver.value = false;
};

const onDrop = event => {
  event.preventDefault();
  const beforeTaskId = hoverBeforeId.value;
  hoverBeforeId.value = null;
  isOver.value = false;
  emit('drop-card', { stepId: props.step.id, beforeTaskId });
};
</script>

<template>
  <section
    class="rk-column"
    :class="{ 'rk-column--over': isOver, 'rk-column--cancelled': step.cancelled }"
    @dragover="onColumnDragOver"
    @dragleave="onColumnDragLeave"
    @drop="onDrop"
  >
    <header class="rk-column__head">
      <span class="rk-column__dot" :style="{ background: step.color || '#4F46E5' }" />
      <h3 class="rk-column__title" :title="step.name">{{ step.name }}</h3>
      <span class="rk-column__count">{{ tasks.length }}</span>
      <button
        type="button"
        class="rk-column__edit"
        title="Editar etapa"
        @click="emit('edit-step', step)"
      >
        Editar
      </button>
    </header>

    <p v-if="step.description" class="rk-column__description">
      {{ step.description }}
    </p>
    <span v-if="step.cancelled" class="rk-column__flag">Etapa de perdido</span>
    <span v-if="formattedTotal" class="rk-column__total">{{ formattedTotal }}</span>

    <div class="rk-column__body">
      <RightKanbanCard
        v-for="task in tasks"
        :key="task.id"
        :task="task"
        :account-id="accountId"
        :dragging="task.id === draggingTaskId"
        :drop-before="task.id === hoverBeforeId"
        @dragstart="onCardDragStart"
        @dragend="onCardDragEnd"
        @dragover="onCardDragOver"
      />

      <p v-if="!tasks.length" class="rk-column__empty">
        Nenhum card nesta etapa. Arraste um card para ca.
      </p>
    </div>
  </section>
</template>

<style scoped>
.rk-column {
  display: flex;
  flex: 0 0 288px;
  flex-direction: column;
  gap: 6px;
  width: 288px;
  padding: 10px;
  background: #f1f5f9;
  border: 1px solid transparent;
  border-radius: 10px;
}

.rk-column--over {
  border-color: #4f46e5;
  background: #eef2ff;
}

.rk-column--cancelled {
  background: #fef2f2;
}

.rk-column__head {
  display: flex;
  gap: 6px;
  align-items: center;
}

.rk-column__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.rk-column__title {
  margin: 0;
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rk-column__count {
  padding: 0 6px;
  font-size: 11px;
  color: #475569;
  background: #e2e8f0;
  border-radius: 999px;
}

.rk-column__edit {
  padding: 2px 6px;
  margin-left: auto;
  font-size: 11px;
  color: #475569;
  cursor: pointer;
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
}

.rk-column__edit:hover {
  color: #1e293b;
  background: #e2e8f0;
}

.rk-column__description {
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
}

.rk-column__flag {
  align-self: flex-start;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 4px;
}

.rk-column__total {
  font-size: 11px;
  font-weight: 600;
  color: #047857;
}

.rk-column__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 60px;
  padding-top: 4px;
  overflow-y: auto;
}

.rk-column__empty {
  padding: 12px 8px;
  margin: 0;
  font-size: 11px;
  color: #94a3b8;
  text-align: center;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
}

.dark .rk-column {
  background: #0f172a;
}

.dark .rk-column--over {
  background: #1e1b4b;
}

.dark .rk-column--cancelled {
  background: #27151a;
}

.dark .rk-column__title {
  color: #e2e8f0;
}

.dark .rk-column__count {
  background: #334155;
  color: #cbd5e1;
}

.dark .rk-column__edit {
  border-color: #334155;
  color: #cbd5e1;
}

.dark .rk-column__empty {
  border-color: #334155;
  color: #64748b;
}
</style>
